import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import styled from '@emotion/styled';
import { assessmentValidateCode, assessmentValidateToken, assessmentLoad } from 'actions/assessment';
import { setLocalSignInRedirect } from 'actions/app';
import { getLocalizedPath } from 'helpers/getLocale';
import { rememberVoucherCredential, storeRedeemedAssessmentSession } from '../sessionStorage';

const BODY_FONT = 'Rubik-Regular, system-ui, sans-serif';

const Heading = styled(Typography)({
  fontSize: 26,
  fontWeight: 700,
  textAlign: 'left',
  marginBottom: 12,
  lineHeight: 1.3,
});

const Lede = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 15,
  color: theme.palette.grey[700],
  textAlign: 'left',
  marginBottom: 24,
  lineHeight: 1.55,
}));

const ExpectTitle = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 13,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 1.2,
  color: theme.palette.grey[600],
  marginBottom: 12,
}));

const BulletList = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: '0 0 28px 0',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

const Bullet = styled('li')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  fontFamily: BODY_FONT,
  fontSize: 14,
  lineHeight: 1.5,
  color: theme.palette.grey[800],
  '&:before': {
    content: '"✓"',
    color: theme.palette.primary.main,
    fontWeight: 700,
    flexShrink: 0,
    width: 16,
    textAlign: 'center',
  },
}));

const FinePrint = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 13,
  color: theme.palette.grey[600],
  textAlign: 'left',
  marginBottom: 20,
  lineHeight: 1.5,
}));

const ButtonStack = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

const ButtonRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 12,
  marginBottom: 12,
  '& > *': { flex: 1 },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const PrimaryButton = styled(Button)({
  fontFamily: BODY_FONT,
  borderRadius: 25,
  padding: '14px 24px',
  fontSize: 16,
  // 500 matches SecondaryButton and the theme contained-primary default.
  // Was 600 — that's the only thing that visually distinguished it from
  // the rest of the app's CTA family.
  fontWeight: 500,
  textTransform: 'none',
});

const SecondaryButton = styled(Button)(({ theme }) => ({
  fontFamily: BODY_FONT,
  borderRadius: 25,
  padding: '14px 24px',
  // Match PrimaryButton (16). The 15 / 16 split was a sub-pixel hierarchy
  // tell that read as "misaligned" next to the primary; weight (500 vs
  // 600) already carries the hierarchy.
  fontSize: 16,
  fontWeight: 500,
  textTransform: 'none',
  color: theme.palette.grey[700],
}));

const DemoLink = styled(Button)(({ theme }) => ({
  fontFamily: BODY_FONT,
  marginTop: 8,
  textTransform: 'none',
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.grey[600],
  alignSelf: 'center',
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
}));

const ProBlurb = styled('div')(({ theme }) => ({
  marginTop: 16,
  fontFamily: BODY_FONT,
  fontSize: 14,
  lineHeight: 1.5,
  color: theme.palette.grey[600],
  textAlign: 'center',
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    textUnderlineOffset: 2,
  },
  '& a:hover': {
    color: theme.palette.primary.dark,
  },
}));

const SignInLink = styled(Button)(({ theme }) => ({
  fontFamily: BODY_FONT,
  marginTop: 4,
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 400,
  color: theme.palette.grey[400],
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  alignSelf: 'center',
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.grey[600],
    textDecoration: 'underline',
  },
}));

const CodeInput = styled(TextField)({
  marginBottom: 16,
  '& .MuiOutlinedInput-root': {
    borderRadius: 14,
    fontFamily: 'monospace',
    fontSize: 18,
    letterSpacing: 2,
  },
  '& input': {
    textAlign: 'center',
    textTransform: 'uppercase',
  },
});

const ErrorBanner = styled('div')(({ theme }) => ({
  fontFamily: BODY_FONT,
  backgroundColor: '#fdecea',
  border: `1px solid ${theme.palette.error.light}`,
  color: theme.palette.error.dark,
  padding: '12px 16px',
  borderRadius: 12,
  fontSize: 14,
  lineHeight: 1.5,
  marginBottom: 16,
}));

function errorKeyFor(status) {
  if (status === 404) return 'codeError404';
  if (status === 409) return 'codeError409';
  if (status === 400) return 'codeError400';
  return 'codeErrorNetwork';
}

// Mirrors backend/models/assessment_voucher.js generateVoucherCode alphabet
// (no 0/O/1/I to avoid ambiguity). Reject obvious garbage client-side so the
// user gets immediate feedback without a network round-trip.
const VOUCHER_CODE_RE = /^[A-HJ-NP-Z2-9]{8,24}$/;
const normalizeVoucherInput = (raw) =>
  String(raw || '').toUpperCase().replace(/[\s-]/g, '');

// Set to true to surface the "Try the demo" link on the welcome step.
// Hidden for now while we polish the demo flow; the rest of the wiring
// (handler, prop, action, translation) is intact so flipping this back on
// is a one-line change.
const SHOW_DEMO_LINK = false;

export default function Welcome({ onUnlocked, onDemoStart }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const step = location.pathname.endsWith('/code') ? 'code' : 'intro';
  const isRedeem = /\/assessment\/redeem\//.test(location.pathname);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [demoStarting, setDemoStarting] = useState(false);
  // A failed redeem deep-link remounts Welcome across the layout switch, so the
  // error is handed over via router state — see submitToken.
  const [errorKey, setErrorKey] = useState(() => location.state?.redeemErrorKey || null);
  // When the backend signals a completed voucher needs an email gate, flip
  // the form into "review" mode and show an email field. `reviewCode` keeps
  // the original code around across the second submit. A completed-voucher
  // redeem deep-link remounts Welcome across the layout switch, so review
  // state is handed over via router state — see submitToken.
  const [reviewMode, setReviewMode] = useState(() => location.state?.redeemReviewMode || false);
  const [reviewCode, setReviewCode] = useState(() => location.state?.redeemReviewCode || '');
  const [reviewToken, setReviewToken] = useState(() => location.state?.redeemReviewToken || '');
  const [reviewEmail, setReviewEmail] = useState('');

  // Wrap every navigate target through getLocalizedPath so the URL keeps its
  // /<locale>-<country>/ prefix on jobalto.com. Without this, the click pushes
  // a bare /assessment/* path that doesn't match any route on jobalto and falls
  // back to the Welcome screen — which is what made the Purchase button look
  // like it wasn't doing anything.
  const goToPurchase = () => navigate(getLocalizedPath('/assessment/purchase'));
  const goToCode = () => navigate(getLocalizedPath('/assessment/code'));
  const goToSignIn = () => {
    dispatch(setLocalSignInRedirect(getLocalizedPath('/assessment')));
    navigate(getLocalizedPath('/sign-in'));
  };

  const submitCode = async (rawCode, email) => {
    const normalized = normalizeVoucherInput(rawCode);
    if (!VOUCHER_CODE_RE.test(normalized)) {
      setErrorKey(errorKeyFor(404));
      return;
    }
    setSubmitting(true);
    setErrorKey(null);
    try {
      const result = await dispatch(assessmentValidateCode(normalized, email));
      // Completed-voucher re-view path. The backend returns one of three
      // `reviewMode` values; everything else falls through to the normal
      // start-of-assessment flow.
      if (result?.reviewMode === 'awaiting_email') {
        setReviewMode(true);
        setReviewCode(normalized);
        setSubmitting(false);
        return;
      }
      if (result?.reviewMode === 'email_mismatch') {
        setReviewMode(true);
        setReviewCode(normalized);
        setErrorKey('codeErrorEmailMismatch');
        setSubmitting(false);
        return;
      }
      storeRedeemedAssessmentSession(result);
      rememberVoucherCredential('code', normalized, result?.voucherId);
      onUnlocked?.(result);
    } catch (err) {
      const status = err?.status || err?.response?.status || err?.error?.code;
      setErrorKey(errorKeyFor(status));
      setSubmitting(false);
    }
  };

  const handleSubmit = (event) => {
    event?.preventDefault?.();
    if (submitting) return;
    if (reviewMode) {
      if (!reviewEmail.trim()) return;
      if (reviewToken) submitToken(reviewToken, reviewEmail.trim());
      else submitCode(reviewCode, reviewEmail.trim());
      return;
    }
    if (!code.trim()) return;
    submitCode(code);
  };

  // Auto-redeem when arriving at /assessment/code?code=XXXXXXX (e.g. from the
  // resume-link email sent during the info wizard). Strips the param from the
  // URL so a hard-refresh after a failed redeem doesn't re-fire the submit.
  const autoRedeemAttempted = useRef(false);
  useEffect(() => {
    if (step !== 'code' || autoRedeemAttempted.current) return;
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('code');
    if (!fromUrl) return;
    autoRedeemAttempted.current = true;
    setCode(fromUrl);
    params.delete('code');
    const nextSearch = params.toString();
    navigate(
      `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`,
      { replace: true },
    );
    submitCode(fromUrl);
    // submitCode is intentionally not in deps — we want this to fire exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, location.search, location.pathname, navigate]);

  // Redeem a voucher by its long token — used by the /assessment/redeem/:token
  // deep-link in the proVoucher email. On failure, fall back to the manual
  // code-entry screen (this component stays mounted, so the error carries over).
  const submitToken = async (token, email) => {
    setSubmitting(true);
    setErrorKey(null);
    try {
      const result = await dispatch(assessmentValidateToken(token, email));
      if (result?.reviewMode === 'awaiting_email' || result?.reviewMode === 'email_mismatch') {
        const mismatch = result.reviewMode === 'email_mismatch';
        setReviewMode(true);
        setReviewToken(token);
        if (result.code) setReviewCode(result.code);
        if (mismatch) setErrorKey('codeErrorEmailMismatch');
        setSubmitting(false);
        // Welcome remounts across the redeem→code layout switch, so the review
        // state travels via router state to survive into the code-entry screen.
        navigate(getLocalizedPath('/assessment/code'), {
          replace: true,
          state: {
            redeemReviewMode: true,
            redeemReviewCode: result.code || '',
            redeemReviewToken: token,
            ...(mismatch ? { redeemErrorKey: 'codeErrorEmailMismatch' } : {}),
          },
        });
        return;
      }
      storeRedeemedAssessmentSession(result);
      rememberVoucherCredential('token', token, result?.voucherId);
      onUnlocked?.(result);
    } catch (err) {
      const status = err?.status || err?.response?.status || err?.error?.code;
      const key = errorKeyFor(status);
      setErrorKey(key);
      setSubmitting(false);
      // Welcome remounts across the redeem→code layout switch, so the error
      // travels via router state to survive into the code-entry screen.
      navigate(getLocalizedPath('/assessment/code'), { replace: true, state: { redeemErrorKey: key } });
    }
  };

  const autoTokenAttempted = useRef(false);
  useEffect(() => {
    if (autoTokenAttempted.current) return;
    const m = location.pathname.match(/\/assessment\/redeem\/([^/?#]+)/);
    if (!m) return;
    autoTokenAttempted.current = true;
    submitToken(decodeURIComponent(m[1]));
    // submitToken intentionally excluded — fire exactly once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleDemo = async () => {
    if (demoStarting) return;
    setDemoStarting(true);
    try {
      const result = await dispatch(assessmentLoad({ createSession: true }));
      storeRedeemedAssessmentSession(result);
      sessionStorage.setItem('isDemo', '1');
      onDemoStart?.(result);
    } catch (err) {
      console.error('Demo start failed:', err);
      setDemoStarting(false);
    }
  };

  // Voucher redeem deep-link in progress — show a spinner. On success the
  // parent advances the flow; on failure submitToken navigates to /code.
  if (isRedeem) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '96px 0' }}>
        <CircularProgress />
      </div>
    );
  }

  if (step === 'intro') {
    return (
      <>
        <Heading variant="h1">{t('Assessment.welcomeHeading')}</Heading>
        <Lede>{t('Assessment.welcomeLede')}</Lede>

        <ExpectTitle>{t('Assessment.welcomeWhatToExpect')}</ExpectTitle>
        <BulletList>
          <Bullet>{t('Assessment.welcomeBullet1')}</Bullet>
          <Bullet>{t('Assessment.welcomeBullet2')}</Bullet>
          <Bullet>{t('Assessment.welcomeBullet3')}</Bullet>
          <Bullet>{t('Assessment.welcomeBullet4')}</Bullet>
          <Bullet>{t('Assessment.welcomeBullet5')}</Bullet>
        </BulletList>

        <FinePrint>{t('Assessment.welcomeFinePrint')}</FinePrint>

        <ButtonRow>
          <PrimaryButton variant="contained" color="primary" size="large" onClick={goToCode}>
            {t('Assessment.welcomeHaveCode')}
          </PrimaryButton>
          <SecondaryButton variant="outlined" size="large" onClick={goToPurchase}>
            {t('Assessment.welcomePurchase')}
          </SecondaryButton>
        </ButtonRow>
        <ButtonStack>
          {SHOW_DEMO_LINK ? (
            <DemoLink size="small" onClick={handleDemo} disabled={demoStarting}>
              {demoStarting ? <CircularProgress size={16} /> : t('Assessment.welcomeTryDemo')}
            </DemoLink>
          ) : (
            <ProBlurb>
              <Trans
                i18nKey="Assessment.welcomeProBlurb"
                components={{ a: <a href="/pro">pro</a> }}
              />
            </ProBlurb>
          )}
          <SignInLink size="small" onClick={goToSignIn}>
            {t('Assessment.welcomeOptionalSignIn')}
          </SignInLink>
        </ButtonStack>
      </>
    );
  }

  if (reviewMode) {
    // Voucher was already completed. The backend asked for the email that the
    // user originally provided during the assessment. Matching email → server
    // mints a fresh session token and we navigate to results via onUnlocked.
    return (
      <form onSubmit={handleSubmit}>
        <Heading variant="h1">{t('Assessment.codeReviewHeading')}</Heading>
        <Lede>{t('Assessment.codeReviewLede')}</Lede>

        {errorKey && <ErrorBanner role="alert">{t(`Assessment.${errorKey}`)}</ErrorBanner>}

        <CodeInput
          fullWidth
          autoFocus
          type="email"
          value={reviewEmail}
          onChange={(e) => setReviewEmail(e.target.value)}
          placeholder={t('Assessment.codeReviewEmailPlaceholder')}
          disabled={submitting}
          slotProps={{
            htmlInput: { maxLength: 254, inputMode: 'email', autoComplete: 'email', 'aria-label': t('Assessment.codeReviewEmailPlaceholder') }
          }}
        />

        <ButtonRow>
          <PrimaryButton
            type="submit"
            variant="contained"
            color="primary"
            disabled={!reviewEmail.trim() || submitting}
            sx={{ padding: '12px 28px', fontSize: 15 }}
          >
            {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : t('Assessment.codeReviewUnlock')}
          </PrimaryButton>
          <SecondaryButton
            variant="outlined"
            onClick={() => { setReviewMode(false); setReviewCode(''); setReviewToken(''); setReviewEmail(''); setErrorKey(null); }}
            disabled={submitting}
            sx={{ padding: '12px 28px', fontSize: 15 }}
          >
            {t('Assessment.codeReviewCancel')}
          </SecondaryButton>
        </ButtonRow>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Heading variant="h1">{t('Assessment.codeHeading')}</Heading>
      <Lede>{t('Assessment.codeLede')}</Lede>

      {location.state?.sessionRecovery && (
        <ErrorBanner role="status">{t('Assessment.codeSessionRecovery')}</ErrorBanner>
      )}

      {errorKey && <ErrorBanner role="alert">{t(`Assessment.${errorKey}`)}</ErrorBanner>}

      <CodeInput
        fullWidth
        autoFocus
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder={t('Assessment.codePlaceholder')}
        disabled={submitting}
        slotProps={{
          htmlInput: { maxLength: 24, 'aria-label': t('Assessment.codeHeading') }
        }}
      />

      <ButtonRow>
        <PrimaryButton
          type="submit"
          variant="contained"
          color="primary"
          disabled={!code.trim() || submitting}
          sx={{ padding: '12px 28px', fontSize: 15 }}
        >
          {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : t('Assessment.codeUnlock')}
        </PrimaryButton>
        <SecondaryButton
          variant="outlined"
          onClick={goToPurchase}
          disabled={submitting}
          sx={{ padding: '12px 28px', fontSize: 15 }}
        >
          {t('Assessment.codeNoCode')}
        </SecondaryButton>
      </ButtonRow>
    </form>
  );
}
